import { useQuery } from 'react-apollo';
import produce from 'immer';

import { AlbumTeamSplitPhotos } from '../../../../../../graphql/queries/albums';
import { ALBUM_PHOTO_FILTERS } from '../../../../../../constants/albums';
import { ALBUM_FOLDER_TAG_SEARCH_FILTER_TYPE } from '../../../../../../constants/album-folder-tags';
import { getTeamTag, hasTeamTag } from '../../../../../../util/album';

const DEFAULT_LIMIT = 100;

// Single source of truth for the variables of the two tag-filtered split
// queries. Used both by the live `useQuery` calls here and by the in-place
// mutation cache splices (`spliceTeamSplitPhotos`) so a `readQuery` always
// matches the cached query exactly. A mismatch would be a cache miss and the
// splice would silently no-op. `mode` is a CollectionFilterModeEnum value
// (ALL | NONE); `teamTagId` is the team album folder tag id.
export const buildTeamSplitVariables = ({
  mode,
  teamTagId,
  albumId,
  albumPhotoFilter,
  uploaderIds,
  sortByShootTime,
  filenameSearchQuery,
  objectSearchQuery,
  offset = 0,
  limit = DEFAULT_LIMIT,
}) => ({
  id: albumId,
  ignore_watermark: true,
  limit,
  offset,
  album_photo_filter: albumPhotoFilter || ALBUM_PHOTO_FILTERS.PUBLISHED,
  uploader_ids: uploaderIds || [],
  sort_by_shoot_time: sortByShootTime || false,
  original_filename: filenameSearchQuery || null,
  search_query: objectSearchQuery || null,
  album_folder_tag_ids: [teamTagId],
  album_folder_tag_ids_mode: mode,
});

// Refetch descriptors for the two split queries. Empty when there is no team
// tag, so it is a safe no-op for non-team albums and shared mutation HOCs.
// Used only to reconcile exact per-section totals after a select-all mutation.
export const getTeamSplitRefetchQueries = ({ teamTagId, splitVarsBase }) => {
  if (!teamTagId) {
    return [];
  }

  return [
    ALBUM_FOLDER_TAG_SEARCH_FILTER_TYPE.ALL,
    ALBUM_FOLDER_TAG_SEARCH_FILTER_TYPE.NONE,
  ].map(mode => ({
    query: AlbumTeamSplitPhotos,
    variables: buildTeamSplitVariables({ mode, teamTagId, ...splitVarsBase }),
  }));
};

// In-place cache splice of the two split query docs, mirroring the existing
// `photos_ungrouped` splice in every photo mutation. Removes the given photo
// ids from whichever section they belong to and decrements that section's
// total/count. Guarded, so it is a no-op when the split queries are not in the
// cache (no team tag, or a shared HOC's non-team consumer).
//
// For a select-all the exact per-section total is not derivable client-side
// (ignored ids span both sections); the visible list is cleared to the ignored
// ids and the counts are reconciled by a refetch in the select-all branch of
// the caller (see `getTeamSplitRefetchQueries`).
export const spliceTeamSplitPhotos = (
  store,
  {
    teamTagId,
    splitVarsBase,
    removedIds = [],
    ignoredIds = [],
    isSelectAll = false,
  },
) => {
  if (!teamTagId) {
    return;
  }

  const removedSet = new Set(removedIds);
  const ignoredSet = new Set(ignoredIds);

  [
    ALBUM_FOLDER_TAG_SEARCH_FILTER_TYPE.ALL,
    ALBUM_FOLDER_TAG_SEARCH_FILTER_TYPE.NONE,
  ].forEach(mode => {
    const variables = buildTeamSplitVariables({
      mode,
      teamTagId,
      ...splitVarsBase,
    });

    let data;
    try {
      data = store.readQuery({ query: AlbumTeamSplitPhotos, variables });
    } catch (error) {
      // Query not in the cache for this doc - nothing to splice.
      return;
    }

    if (!data?.album?.photos) {
      return;
    }

    const currentRecords = data.album.photos.records;
    const remainingRecords = isSelectAll
      ? currentRecords.filter(({ id }) => ignoredSet.has(id))
      : currentRecords.filter(({ id }) => !removedSet.has(id));
    const removedFromThisDoc = currentRecords.length - remainingRecords.length;

    const updatedData = produce(data, draft => {
      draft.album.photos.records = remainingRecords;
      draft.album.photos.total = Math.max(
        0,
        draft.album.photos.total - removedFromThisDoc,
      );
      draft.album.photos.count = Math.max(
        0,
        draft.album.photos.count - removedFromThisDoc,
      );
    });

    store.writeQuery({
      query: AlbumTeamSplitPhotos,
      variables,
      data: updatedData,
    });
  });
};

// Moves photos between the two split query docs in-place when their team-tag
// membership changes (add/remove of the TEAM tag). `toTeam` true moves from the
// individual (NONE) doc into the team (ALL) doc, false does the reverse. Guarded
// so it is a no-op when the split queries are not cached. Uses the `client`
// (ApolloClient) since this runs outside a mutation `update()`.
export const moveTeamSplitPhotos = (
  client,
  { teamTagId, splitVarsBase, photoIds, toTeam },
) => {
  if (!teamTagId || !photoIds?.length) {
    return;
  }

  const srcMode = toTeam
    ? ALBUM_FOLDER_TAG_SEARCH_FILTER_TYPE.NONE
    : ALBUM_FOLDER_TAG_SEARCH_FILTER_TYPE.ALL;
  const destMode = toTeam
    ? ALBUM_FOLDER_TAG_SEARCH_FILTER_TYPE.ALL
    : ALBUM_FOLDER_TAG_SEARCH_FILTER_TYPE.NONE;
  const srcVars = buildTeamSplitVariables({
    mode: srcMode,
    teamTagId,
    ...splitVarsBase,
  });
  const destVars = buildTeamSplitVariables({
    mode: destMode,
    teamTagId,
    ...splitVarsBase,
  });
  const idSet = new Set(photoIds);

  let srcData;
  try {
    srcData = client.readQuery({
      query: AlbumTeamSplitPhotos,
      variables: srcVars,
    });
  } catch (error) {
    // Source split query not cached - split not active, nothing to move.
    return;
  }

  if (!srcData?.album?.photos) {
    return;
  }

  const movedRecords = srcData.album.photos.records.filter(({ id }) =>
    idSet.has(id),
  );

  if (!movedRecords.length) {
    // The photos aren't in the loaded source page (e.g. paginated out), so we
    // can't move the record objects; leave the cache for a later refetch.
    return;
  }

  const srcUpdated = produce(srcData, draft => {
    draft.album.photos.records = draft.album.photos.records.filter(
      ({ id }) => !idSet.has(id),
    );
    draft.album.photos.total = Math.max(
      0,
      draft.album.photos.total - movedRecords.length,
    );
    draft.album.photos.count = Math.max(
      0,
      draft.album.photos.count - movedRecords.length,
    );
  });
  client.writeQuery({
    query: AlbumTeamSplitPhotos,
    variables: srcVars,
    data: srcUpdated,
  });

  let destData;
  try {
    destData = client.readQuery({
      query: AlbumTeamSplitPhotos,
      variables: destVars,
    });
  } catch (error) {
    // Destination not cached; the source removal above still stands and a
    // refetch will populate the destination.
    return;
  }

  if (!destData?.album?.photos) {
    return;
  }

  const destUpdated = produce(destData, draft => {
    const existingIds = new Set(draft.album.photos.records.map(({ id }) => id));
    const toAdd = movedRecords.filter(({ id }) => !existingIds.has(id));
    draft.album.photos.records = [...toAdd, ...draft.album.photos.records];
    draft.album.photos.total += toAdd.length;
    draft.album.photos.count += toAdd.length;
  });
  client.writeQuery({
    query: AlbumTeamSplitPhotos,
    variables: destVars,
    data: destUpdated,
  });
};

// Runs the two tag-filtered photo queries (team = mode ALL, individual = mode
// NONE) that back the TEAM PHOTOS / INDIVIDUAL PHOTOS split on the album
// listing. Falls back (isTeamSplitEnabled === false) when there is no team tag
// or either section is empty, so the caller renders today's single grid.
const useTeamSplitPhotos = ({
  albumId,
  albumFolderTags,
  albumPhotoFilter,
  uploaderIds,
  sortByShootTime,
  filenameSearchQuery,
  objectSearchQuery,
  isContributor,
}) => {
  const teamTag = hasTeamTag(albumFolderTags)
    ? getTeamTag(albumFolderTags)
    : undefined;
  const teamTagId = teamTag?.id;

  const splitVarsBase = {
    albumId,
    albumPhotoFilter,
    uploaderIds,
    sortByShootTime,
    filenameSearchQuery,
    objectSearchQuery,
  };

  const skip = !albumId || !teamTag || isContributor;

  const commonOptions = {
    skip,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  };

  const teamQuery = useQuery(AlbumTeamSplitPhotos, {
    ...commonOptions,
    variables: buildTeamSplitVariables({
      mode: ALBUM_FOLDER_TAG_SEARCH_FILTER_TYPE.ALL,
      teamTagId,
      ...splitVarsBase,
    }),
  });

  const individualQuery = useQuery(AlbumTeamSplitPhotos, {
    ...commonOptions,
    variables: buildTeamSplitVariables({
      mode: ALBUM_FOLDER_TAG_SEARCH_FILTER_TYPE.NONE,
      teamTagId,
      ...splitVarsBase,
    }),
  });

  const teamPhotos = teamQuery.data?.album?.photos;
  const individualPhotos = individualQuery.data?.album?.photos;
  const teamTotal = teamPhotos?.total ?? 0;
  const individualTotal = individualPhotos?.total ?? 0;

  const isTeamSplitEnabled = !!teamTag && teamTotal > 0 && individualTotal > 0;

  const makeLoadMore = query => () =>
    query.fetchMore({
      variables: { offset: query.data.album.photos.records.length },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return previousResult;
        }

        return {
          ...fetchMoreResult,
          album: {
            ...fetchMoreResult.album,
            photos: {
              ...fetchMoreResult.album.photos,
              count:
                previousResult.album.photos.count +
                fetchMoreResult.album.photos.count,
              records: [
                ...previousResult.album.photos.records,
                ...fetchMoreResult.album.photos.records,
              ],
            },
          },
        };
      },
    });

  return {
    isTeamSplitEnabled,
    teamPhotos,
    individualPhotos,
    teamTotal,
    individualTotal,
    teamLoadMore: makeLoadMore(teamQuery),
    individualLoadMore: makeLoadMore(individualQuery),
    teamTagId,
    splitVarsBase,
    loading: teamQuery.loading || individualQuery.loading,
  };
};

export default useTeamSplitPhotos;
